import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge, getTierColor, getTierGlow } from '@/types/badges';
import { X, Sparkles, Share2, Loader2, Download, Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  generateShareImage,
  getShareCaption,
  shareWithImage,
  downloadImage,
  getRandomQuote,
} from '@/utils/shareImageGenerator';

// Social media icons
const TwitterIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

interface BadgeUnlockModalProps {
  badge: Badge | null;
  userName: string;
  daysActive: number;
  tier: string;
  referralCode?: string;
  onClose: () => void;
}

export function BadgeUnlockModal({ 
  badge, 
  userName, 
  daysActive, 
  tier,
  referralCode,
  onClose 
}: BadgeUnlockModalProps) {
  const [view, setView] = useState<'badge' | 'share'>('badge');
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const [copied, setCopied] = useState(false);

  // Reset view when badge changes
  useEffect(() => {
    if (badge) {
      setView('badge');
      setPreviewUrl(null);
      setImageBlob(null);
    }
  }, [badge]);

  if (!badge) return null;

  const displayName = userName || 'Anonymous';
  const shareableQuote = badge.shareableQuote.replace('[Name]', displayName);
  const motivationalQuote = getRandomQuote();
  const caption = getShareCaption('badge', displayName, daysActive, tier, referralCode, badge);

  const handleShareClick = async () => {
    setIsGenerating(true);
    try {
      const blob = await generateShareImage({
        type: 'badge',
        userName: displayName,
        daysActive,
        tier,
        badge,
      });
      setImageBlob(blob);
      const url = URL.createObjectURL(blob);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      setView('share');
    } catch (err) {
      console.error('Preview generation error:', err);
      toast.error('Unable to generate image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleBack = () => {
    setView('badge');
  };

  const handleNativeShare = async () => {
    if (!imageBlob) return;
    try {
      const shared = await shareWithImage(imageBlob, caption);
      if (!shared) {
        downloadImage(imageBlob);
        await navigator.clipboard.writeText(caption);
        toast.success('Image downloaded & caption copied!');
      } else {
        toast.success('Shared successfully!');
      }
    } catch (err) {
      toast.error('Unable to share. Please try again.');
    }
  };

  const handleDownload = () => {
    if (!imageBlob) return;
    downloadImage(imageBlob);
    toast.success('Image downloaded!');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setCopied(true);
      toast.success('Caption copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Unable to copy.');
    }
  };

  const shareToTwitter = () => {
    if (imageBlob) downloadImage(imageBlob, 'evolve-share-twitter.png');
    const text = encodeURIComponent(caption);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank', 'width=550,height=420');
    if (imageBlob) toast.success('Image downloaded! Attach it to your tweet.');
  };

  const shareToFacebook = () => {
    if (imageBlob) downloadImage(imageBlob, 'evolve-share-facebook.png');
    const text = encodeURIComponent(caption);
    window.open(`https://www.facebook.com/sharer/sharer.php?quote=${text}`, '_blank', 'width=550,height=420');
    if (imageBlob) toast.success('Image downloaded! Attach it to your post.');
  };

  const shareToInstagram = async () => {
    if (imageBlob) downloadImage(imageBlob, 'evolve-share-instagram.png');
    await navigator.clipboard.writeText(caption);
    toast.success('Image downloaded & caption copied! Open Instagram to share.');
  };

  return (
    <Dialog open={!!badge} onOpenChange={() => onClose()}>
      <DialogContent className="bg-background/95 backdrop-blur-xl border-white/10 max-w-sm p-0 overflow-hidden rounded-2xl [&>button]:hidden">
        {/* Animated background gradient */}
        <div className={cn(
          "absolute inset-0 opacity-20",
          `bg-gradient-to-br ${getTierColor(badge.tier)}`
        )} />
        
        {/* Close button - only show in badge view */}
        {view === 'badge' && (
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 z-20 p-1.5 rounded-full bg-white/10 text-muted-foreground hover:text-foreground hover:bg-white/20 transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        {view === 'badge' ? (
          /* Badge Unlock View */
          <div className="relative z-10 p-6 flex flex-col items-center text-center">
            {/* Animated particles */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-4 left-6 animate-bounce" style={{ animationDelay: '0.1s' }}>
                <Sparkles className="w-5 h-5 text-yellow-400/70" />
              </div>
              <div className="absolute top-8 right-8 animate-bounce" style={{ animationDelay: '0.3s' }}>
                <Sparkles className="w-4 h-4 text-yellow-400/60" />
              </div>
            </div>

            {/* Badge Icon with glow */}
            <div className={cn(
              "relative w-24 h-24 rounded-full flex items-center justify-center mb-4",
              `bg-gradient-to-br ${getTierColor(badge.tier)}`,
              `shadow-2xl ${getTierGlow(badge.tier)}`
            )}>
              <div className={cn(
                "absolute inset-0 rounded-full animate-ping opacity-20",
                `bg-gradient-to-br ${getTierColor(badge.tier)}`
              )} />
              <span className="text-4xl relative z-10">{badge.icon}</span>
            </div>

            {/* Badge Name */}
            <h2 className="text-xl font-bold text-foreground mb-2">
              {badge.name}
            </h2>
            
            {/* Tier badge pill */}
            <span className={cn(
              "px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3",
              `bg-gradient-to-r ${getTierColor(badge.tier)} text-white shadow-lg`
            )}>
              {badge.tier}
            </span>

            {/* Condition */}
            <p className="text-sm text-muted-foreground mb-3">
              {badge.condition}
            </p>

            {/* Quote card */}
            <div className="bg-white/5 backdrop-blur rounded-xl p-3 mb-3 w-full border border-white/10">
              <p className="text-sm font-medium text-foreground">
                "{shareableQuote}"
              </p>
              <div className="mt-2 flex items-center justify-center gap-2 text-xs">
                <span className={cn(
                  "font-bold",
                  `bg-gradient-to-r ${getTierColor(badge.tier)} bg-clip-text text-transparent`
                )}>
                  {tier}
                </span>
                <span className="text-muted-foreground">•</span>
                <span className="text-muted-foreground">Day {daysActive}</span>
              </div>
            </div>

            {/* Motivational quote */}
            <p className="text-xs text-muted-foreground italic mb-4 px-2">
              "{motivationalQuote}"
            </p>

            {/* Share Button */}
            <Button
              onClick={handleShareClick}
              disabled={isGenerating}
              className="w-full bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-semibold"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Share2 className="w-4 h-4 mr-2" />
                  Share Achievement
                </>
              )}
            </Button>

            {/* Continue Button */}
            <Button
              onClick={onClose}
              variant="ghost"
              className="w-full mt-2 text-muted-foreground hover:text-foreground"
            >
              Continue
            </Button>
          </div>
        ) : (
          /* Share Preview View */
          <div className="relative z-10 p-4 pt-8 flex flex-col">
            {/* Image Preview */}
            {previewUrl && (
              <div className="rounded-xl overflow-hidden border border-white/10 mb-4 shadow-xl">
                <img 
                  src={previewUrl} 
                  alt="Share preview" 
                  className="w-full aspect-square object-cover"
                />
              </div>
            )}

            {/* Social buttons */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              <Button
                onClick={shareToFacebook}
                size="sm"
                variant="outline"
                className="flex flex-col items-center gap-1 h-auto py-3 bg-[#1877F2]/10 border-[#1877F2]/30 hover:bg-[#1877F2]/20 text-[#1877F2]"
              >
                <FacebookIcon />
                <span className="text-[10px]">Facebook</span>
              </Button>
              <Button
                onClick={shareToTwitter}
                size="sm"
                variant="outline"
                className="flex flex-col items-center gap-1 h-auto py-3 bg-black/50 border-white/10 hover:bg-black/70 text-white"
              >
                <TwitterIcon />
                <span className="text-[10px]">X</span>
              </Button>
              <Button
                onClick={shareToInstagram}
                size="sm"
                variant="outline"
                className="flex flex-col items-center gap-1 h-auto py-3 bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-pink-500/30 hover:from-purple-500/30 hover:to-pink-500/30 text-pink-400"
              >
                <InstagramIcon />
                <span className="text-[10px]">Instagram</span>
              </Button>
              <Button
                onClick={handleDownload}
                size="sm"
                variant="outline"
                className="flex flex-col items-center gap-1 h-auto py-3 border-white/10 hover:bg-white/10"
              >
                <Download className="w-4 h-4" />
                <span className="text-[10px]">Download</span>
              </Button>
            </div>

            {/* Copy caption */}
            <Button
              onClick={handleCopy}
              variant="ghost"
              size="sm"
              className="w-full text-muted-foreground hover:text-foreground"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2 text-green-500" />
                  Caption Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Caption
                </>
              )}
            </Button>

            {/* Done button */}
            <Button
              onClick={onClose}
              variant="ghost"
              className="w-full mt-2 text-muted-foreground hover:text-foreground"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
